import React from "react";

import { formatIsoMonth } from "@/utils/dates";

export interface MissingBusinessFeeApartment {
    id: number;
    name: string;
    /**
     * The first day of the earliest operational month no business fee entry
     * covers, as the readiness endpoint reported it
     * (`manual-business-fee/FR-020`).
     */
    firstUncoveredMonth: string;
}

export interface MissingBusinessFeesBannerProps {
    /**
     * Apartments with at least one operational month no business fee covers.
     *
     * NOT "apartments with no fee": the readiness rule deliberately also reports
     * an apartment whose earliest entry post-dates its first operational month
     * (FR-020, AS-015), and that apartment has a fee — four months too late. What
     * every apartment here has in common is the GAP, which is why each carries the
     * month it starts in.
     */
    apartments: MissingBusinessFeeApartment[];
    /**
     * Invoked with an apartment id and the first month it is uncovered for, to
     * start adding a fee THERE. The month is passed on because defaulting to the
     * current month would offer to fill a month that is already covered, leaving
     * the gap — and this notice — exactly where they were.
     */
    onAdd: (apartmentId: number, firstUncoveredMonth: string) => void;
}

/**
 * Warns that some apartments have operational months no business fee covers, and
 * turns each into an actionable entry point. The grid lists only each apartment's
 * CURRENT fee, so a gap in an earlier month is invisible there — and an apartment
 * with no fee at all has no row to "edit" in the first place.
 *
 * The `role="status"` region is mounted UNCONDITIONALLY, empty or not. A polite
 * live region is only announced when its contents CHANGE while it is already in
 * the accessible tree — a region inserted with its text already attached says
 * nothing. Readiness is fetched, so the empty render is always the first one:
 * returning `null` there would make every warning silent. When empty it is
 * `sr-only` rather than `hidden`, because `display: none` would take it back out
 * of the accessible tree and reintroduce exactly the bug.
 */
export const MissingBusinessFeesBanner: React.FC<MissingBusinessFeesBannerProps> = ({ apartments, onAdd }) => {
    const isCovered = apartments.length === 0;

    return (
        <div
            role="status"
            className={
                isCovered
                    ? "sr-only"
                    : "flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800"
            }
        >
            {!isCovered && (
                <>
                    <span className="font-medium">Business fee missing for operational months:</span>
                    {apartments.map((apartment) => {
                        const month = formatIsoMonth(apartment.firstUncoveredMonth);
                        return (
                            <button
                                key={apartment.id}
                                type="button"
                                onClick={() => onAdd(apartment.id, apartment.firstUncoveredMonth)}
                                // The visible text is a subset of the accessible
                                // name (WCAG 2.5.3), and both name the month: a
                                // control that said only "Apartment A" would leave
                                // the operator guessing which month to fill.
                                aria-label={`Add business fee for ${apartment.name} from ${month}`}
                                className="rounded underline decoration-dotted underline-offset-2 hover:decoration-solid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-700"
                            >
                                {apartment.name} from {month}
                            </button>
                        );
                    })}
                </>
            )}
        </div>
    );
};
